import { ActionIcon, Group, Tooltip } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { useTranslation } from "react-i18next";

import { Icon } from "#shared/icons/Icon";
import { Text } from "#shared/view/components/Text";

import { PredictionNegativeFeedbackModal } from "./PredictionNegativeFeedbackModal";
import { useSubmitPredictionFeedback } from "./useSubmitPredictionFeedback";

interface PredictionFeedbackButtonsProps {
  predictionId?: string;
  size?: "xs" | "sm" | "md" | "lg";
  disabled?: boolean;
  predictedMaterialPrice?: number;
  predictedAddedValuePrice?: number;
  predictedTotalPrice?: number;
}

export const PredictionFeedbackButtons = ({
  predictionId,
  size = "xs",
  disabled = false,
  predictedMaterialPrice,
  predictedAddedValuePrice,
  predictedTotalPrice,
}: PredictionFeedbackButtonsProps) => {
  const { t } = useTranslation();
  const { predictionFeedback, submitFeedback, isSubmitting } = useSubmitPredictionFeedback({
    predictionId,
  });
  const [
    isNegativeFeedbackModalOpened,
    { open: openNegativeFeedbackModal, close: closeNegativeFeedbackModal },
  ] = useDisclosure(false);

  if (!predictionId || disabled) {
    return null;
  }

  const currentRating = predictionFeedback?.rating;
  const isThumbsUpActive = currentRating === 1;
  const isThumbsDownActive = currentRating === -1;

  const handleThumbsUp = () => {
    if (isThumbsUpActive) {
      submitFeedback({ rating: 0 });
    } else {
      submitFeedback({ rating: 1 });
    }
  };

  return (
    <Group gap="xs">
      <Text fz="xs" c="dimmed">
        {t("feedback.label")}
      </Text>
      <Group gap={4}>
        <Tooltip label={t("feedback.thumbsUp")}>
          <ActionIcon
            size={size}
            variant={isThumbsUpActive ? "filled" : "subtle"}
            color={isThumbsUpActive ? "green" : "gray"}
            onClick={handleThumbsUp}
            loading={isSubmitting}
            disabled={isSubmitting}
          >
            <Icon name="like" size={12} />
          </ActionIcon>
        </Tooltip>

        <Tooltip label={t("feedback.thumbsDown")}>
          <ActionIcon
            size={size}
            variant={isThumbsDownActive ? "filled" : "subtle"}
            color={isThumbsDownActive ? "red" : "gray"}
            onClick={openNegativeFeedbackModal}
            loading={isSubmitting}
            disabled={isSubmitting}
          >
            <Icon name="dislike" size={12} />
          </ActionIcon>
        </Tooltip>
      </Group>

      {isNegativeFeedbackModalOpened && (
        <PredictionNegativeFeedbackModal
          opened={isNegativeFeedbackModalOpened}
          onClose={closeNegativeFeedbackModal}
          onSubmit={submitFeedback}
          isSubmitting={isSubmitting}
          predictedMaterialPrice={predictedMaterialPrice}
          predictedAddedValuePrice={predictedAddedValuePrice}
          predictedTotalPrice={predictedTotalPrice}
          initialValues={
            isThumbsDownActive && predictionFeedback
              ? {
                  comment: predictionFeedback.comment,
                  causes: predictionFeedback.causes,
                  actualMaterialPrice: predictionFeedback.actualMaterialPrice,
                  actualAddedValuePrice: predictionFeedback.actualAddedValuePrice,
                  actualTotalPrice: predictionFeedback.actualTotalPrice,
                }
              : undefined
          }
        />
      )}
    </Group>
  );
};
